import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, LogOut, Users } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/common/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STORAGE_KEY = "ecopay_user_codes";

// Devises disponibles
const CURRENCIES = [
  { value: "XPF", label: "XPF (Franc Pacifique)" },
  { value: "EUR", label: "EUR (Euro)" },
  { value: "USD", label: "USD (Dollar US)" },
  { value: "AUD", label: "AUD (Dollar Australien)" },
  { value: "NZD", label: "NZD (Dollar Néo-Zélandais)" },
];

const AdminDashboardPage = ({ onAdminLogout }) => {
  const navigate = useNavigate();
  const [userCodes, setUserCodes] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    amount: "",
    currency: "XPF",
    banks: []
  });
  const [newBankName, setNewBankName] = useState("");

  // Charger les codes depuis localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setUserCodes(JSON.parse(stored));
    } else {
      // Code par défaut
      const defaultCodes = [
        { 
          code: "20001", 
          amount: "8645808", 
          currency: "XPF",
          banks: ["Banque de Tahiti", "Banque de Polynésie", "Banque Socredo"],
          createdAt: new Date().toISOString() 
        }
      ];
      setUserCodes(defaultCodes);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultCodes));
    }
  }, []);

  // Sauvegarder dans localStorage
  const saveToStorage = (codes) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
    setUserCodes(codes);
  };

  const handleOpenDialog = (code = null) => {
    if (code) {
      setEditingCode(code);
      setFormData({
        code: code.code,
        amount: code.amount,
        currency: code.currency || "XPF",
        banks: code.banks || []
      });
    } else {
      setEditingCode(null);
      setFormData({ 
        code: "", 
        amount: "", 
        currency: "XPF",
        banks: []
      });
    }
    setNewBankName("");
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingCode(null);
    setFormData({ 
      code: "", 
      amount: "", 
      currency: "XPF",
      banks: []
    });
    setNewBankName("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Vérifier si le code existe déjà (sauf si on édite)
    const codeExists = userCodes.some(
      uc => uc.code === formData.code && (!editingCode || editingCode.code !== formData.code)
    );

    if (codeExists) {
      toast.error("Ce code d'identification existe déjà");
      return;
    }

    // Vérifier qu'au moins une banque est sélectionnée
    if (!formData.banks || formData.banks.length === 0) {
      toast.error("Veuillez ajouter au moins un nom de banque");
      return;
    }

    if (editingCode) {
      // Mise à jour
      const updated = userCodes.map(uc =>
        uc.code === editingCode.code
          ? { ...formData, createdAt: uc.createdAt }
          : uc
      );
      saveToStorage(updated);
      toast.success("Code mis à jour avec succès");
    } else {
      // Création
      const newCode = {
        ...formData,
        createdAt: new Date().toISOString()
      };
      saveToStorage([...userCodes, newCode]);
      toast.success("Code créé avec succès");
    }

    handleCloseDialog();
  };

  const addBankName = () => {
    if (newBankName.trim()) {
      if (!formData.banks.includes(newBankName.trim())) {
        setFormData({
          ...formData,
          banks: [...formData.banks, newBankName.trim()]
        });
        setNewBankName("");
        toast.success("Banque ajoutée");
      } else {
        toast.error("Cette banque existe déjà dans la liste");
      }
    }
  };

  const removeBank = (bankName) => {
    setFormData({
      ...formData,
      banks: formData.banks.filter(b => b !== bankName)
    });
  };

  const handleDelete = (code) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le code ${code} ?`)) {
      const filtered = userCodes.filter(uc => uc.code !== code);
      saveToStorage(filtered);
      toast.success("Code supprimé avec succès");
    }
  };

  const handleLogout = () => {
    onAdminLogout();
    toast.success("Déconnexion réussie");
    navigate("/admin");
  };

  const formatAmount = (amount) => {
    return parseInt(amount).toLocaleString('fr-FR');
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header minimal />
      
      <div className="flex-1 px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2" data-testid="admin-dashboard-title">
                Tableau de bord administrateur
              </h1>
              <p className="text-slate-600">
                Gérez les codes d'identification et les montants associés
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Se déconnecter
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-slate-900">Total codes</h3>
              </div>
              <p className="text-3xl font-bold text-primary">{userCodes.length}</p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Montant total</h3>
              <p className="text-3xl font-bold text-secondary">
                {formatAmount(userCodes.reduce((sum, uc) => sum + parseInt(uc.amount || 0), 0))} XPF
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Montant moyen</h3>
              <p className="text-3xl font-bold text-accent">
                {userCodes.length > 0 
                  ? formatAmount(Math.round(userCodes.reduce((sum, uc) => sum + parseInt(uc.amount || 0), 0) / userCodes.length))
                  : 0
                } XPF
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mb-6">
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-secondary hover:bg-secondary/90 flex items-center gap-2"
              data-testid="create-code-btn"
            >
              <Plus className="h-4 w-4" />
              Créer un nouveau code
            </Button>
          </div>

          {/* Liste des codes */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                      Code d'identification
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                      Montant
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                      Devise
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                      Banques
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                      Date de création
                    </th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {userCodes.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                        Aucun code d'identification. Créez-en un pour commencer.
                      </td>
                    </tr>
                  ) : (
                    userCodes.map((userCode) => (
                      <tr key={userCode.code} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <span className="font-mono font-semibold text-primary">
                            {userCode.code}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-secondary font-semibold">
                            {formatAmount(userCode.amount)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-slate-700 font-medium">
                            {userCode.currency || "XPF"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs text-slate-600">
                            {userCode.banks?.length || 0} banque{(userCode.banks?.length || 0) > 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-600 text-sm">
                          {new Date(userCode.createdAt).toLocaleString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(userCode)}
                              className="hover:bg-blue-50 hover:text-blue-600"
                              data-testid={`edit-code-${userCode.code}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(userCode.code)}
                              className="hover:bg-red-50 hover:text-red-600"
                              data-testid={`delete-code-${userCode.code}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog Créer/Modifier */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="code-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingCode ? "Modifier le code" : "Créer un nouveau code"}
            </DialogTitle>
            <DialogDescription>
              {editingCode 
                ? "Modifiez les informations du code d'identification"
                : "Créez un nouveau code d'identification avec le montant associé"
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label htmlFor="code">Code d'identification</Label>
              <Input
                id="code"
                type="text"
                placeholder="Ex: 20001"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
                data-testid="code-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Montant</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Ex: 8645808"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                min="0"
                data-testid="amount-input"
                className="h-11"
              />
              {formData.amount && formData.currency && (
                <p className="text-sm text-slate-500">
                  Montant formaté : {formatAmount(formData.amount)} {formData.currency}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Devise</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
                required
              >
                <SelectTrigger className="h-11" data-testid="currency-select">
                  <SelectValue placeholder="Sélectionnez la devise" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Noms des banques</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Ex: Banque de Tahiti"
                  value={newBankName}
                  onChange={(e) => setNewBankName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addBankName();
                    }
                  }}
                  className="h-11"
                  data-testid="bank-name-input"
                />
                <Button
                  type="button"
                  onClick={addBankName}
                  className="bg-accent hover:bg-accent/90"
                  data-testid="add-bank-btn"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {formData.banks && formData.banks.length > 0 && (
                <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-slate-50">
                  {formData.banks.map((bankName, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white p-2 rounded border border-slate-200"
                    >
                      <span className="text-sm text-slate-700">{bankName}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBank(bankName)}
                        className="hover:bg-red-50 hover:text-red-600"
                        data-testid={`remove-bank-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-slate-500">
                {formData.banks?.length || 0} banque{(formData.banks?.length || 0) > 1 ? 's' : ''} ajoutée{(formData.banks?.length || 0) > 1 ? 's' : ''}
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="cancel-btn"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-secondary hover:bg-secondary/90"
                data-testid="submit-code-btn"
              >
                {editingCode ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
